import type { CSSProperties } from "react";
import { Card, CardBody, Input, Row } from "reactstrap";

const LicenseCost = (props: any) => {
    const { licenseSettings, lineItems, showLineItems = false } = props;
    const summaryHeaderLabel = props.summaryHeaderLabel !== undefined ? props.summaryHeaderLabel : "Summary";
    const grossHeaderLabel = props.grossHeaderLabel || "Gross cost";
    const baseCostLabel = props.baseCostLabel || "Basic license cost";
    const usersLabel = props.usersLabel || "Additional user cost";
    const sitesLabel = props.sitesLabel || "Additional site cost";
    const additionalUsersCount = Number(props.additionalUsersCount ?? 0);
    const additionalSitesCount = Number(props.additionalSitesCount ?? 0);
    const totalLabel = props.totalLabel || "Total license cost";
    const totalRowClassName = props.totalRowClassName || "";
    const totalValueClassName = props.totalValueClassName || "";
    const totalRowStyle = props.totalRowStyle || {};
    const preSummaryRows = Array.isArray(props.preSummaryRows) ? props.preSummaryRows : [];
    const extraRows = Array.isArray(props.extraRows) ? props.extraRows : [];
    const secondarySummaryHeaderLabel = props.secondarySummaryHeaderLabel || "";
    const hidePrimaryHeader = Boolean(props.hidePrimaryHeader);
    const secondarySummaryValueLabel = props.secondarySummaryValueLabel || "";
    const noCard = Boolean(props.noCard);
    const deferMetricHeadersToSecondarySummary = Boolean(props.deferMetricHeadersToSecondarySummary);

    function formatNumberUK(num: number) {
        return new Intl.NumberFormat('en-GB', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(num);
    }

    const readonlyValues = Boolean(props.readonlyValues);
    const readonlyMetricClass =
        "form-control form-control-sm text-end bg-light-subtle rounded-2 py-1 mb-2";

    const renderReadonlyField = (
        value: string,
        textClassName = "",
        customStyle: CSSProperties = {},
    ) => {
        if (!readonlyValues) {
            return (
                <span className={textClassName} style={customStyle}>
                    {value}
                </span>
            );
        }

        return (
            <Input
                type="text"
                readOnly
                className={`${readonlyMetricClass} ${textClassName}`.trim()}
                style={customStyle}
                value={value}
            />
        );
    };
    const summary = props.licenseSettings?.costSummary;
    if (!summary) return null;
    const renderMoney = (value: number | null | undefined) => `£${formatNumberUK(Number(value || 0))}`;
    const hasDiscount = (value: number | null | undefined) => (Number(value || 0) > 0);
    const renderDiscount = (value: number | null | undefined) =>
        hasDiscount(value) ? `-${Number(value).toFixed(2)}%` : '';
    const hasDiscountColumnsRequested = Boolean(
        (summary?.base?.discountPct ?? 0) > 0 ||
        (summary?.users?.discountPct ?? 0) > 0 ||
        (summary?.sites?.discountPct ?? 0) > 0
    );
    const showDiscountColumns = hasDiscountColumnsRequested;
    const showPrimaryMetricHeaders = showDiscountColumns && !deferMetricHeadersToSecondarySummary;
    const showBaseRow = props.showBaseRow !== false;
    const showUserRow = props.showUserRow === true
        ? true
        : ((summary?.users?.gross ?? 0) > 0 || additionalUsersCount > 0);
    const showSiteRow = props.showSiteRow === true
        ? true
        : props.showSiteRow !== false && ((summary?.sites?.gross ?? 0) > 0 || additionalSitesCount > 0);
    const usersLabelWithCount =
        additionalUsersCount > 0 ? `${usersLabel} (${additionalUsersCount})` : usersLabel;
    const sitesLabelWithCount =
        additionalSitesCount > 0 ? `${sitesLabel} (${additionalSitesCount})` : sitesLabel;

    const table = (
        <table className="w-100">
            <thead>
                {!hidePrimaryHeader && (
                    <tr>
                        <th className="fw-semibold">{summaryHeaderLabel}</th>
                        {showPrimaryMetricHeaders && (
                            <th className="fw-semibold text-end">{grossHeaderLabel}</th>
                        )}
                        {showPrimaryMetricHeaders && (
                            <th className="fw-semibold text-end text-danger">Discount</th>
                        )}
                        <th className="fw-semibold text-end">
                            {!deferMetricHeadersToSecondarySummary ? "Net cost" : ""}
                        </th>
                    </tr>
                )}
            </thead>
            <tbody>
                {preSummaryRows.map((row: any, index: number) => (
                    <tr key={`pre-summary-${index}`}>
                        <td className="fw-bold">{row.label}</td>
                        {showDiscountColumns && <td className="text-end"></td>}
                        {showDiscountColumns && <td className="text-end"></td>}
                        <td className="fw-bold text-end text-success fw-semibold">{renderReadonlyField(renderMoney(row.value))}</td>
                    </tr>
                ))}
                {secondarySummaryHeaderLabel && (
                    <tr>
                        <td className="fw-semibold pt-3">{secondarySummaryHeaderLabel}</td>
                        {showDiscountColumns && (
                            <td className="fw-semibold text-end pt-3">
                                {deferMetricHeadersToSecondarySummary ? grossHeaderLabel : ''}
                            </td>
                        )}
                        {showDiscountColumns && (
                            <td className="fw-semibold text-end text-danger pt-3">
                                {deferMetricHeadersToSecondarySummary ? 'Discount' : ''}
                            </td>
                        )}
                        <td className="fw-semibold text-end pt-3">
                            {deferMetricHeadersToSecondarySummary
                                ? 'Net cost'
                                : renderReadonlyField(secondarySummaryValueLabel)}
                        </td>
                    </tr>
                )}
                {showBaseRow && (
                    <tr>
                        <td className="text-muted" style={{ fontWeight: '400' }}>{baseCostLabel}</td>
                        {showDiscountColumns && (
                            <td className="text-end">
                                {hasDiscount(summary?.base?.discountPct) ? renderReadonlyField(renderMoney(summary?.base?.gross)) : ''}
                            </td>
                        )}
                        {showDiscountColumns && (
                            <td className="text-end text-danger">
                                {renderReadonlyField(
                                    renderDiscount(summary?.base?.discountPct),
                                    "text-danger",
                                )}
                            </td>
                        )}
                        <td className="text-end">{renderReadonlyField(renderMoney(summary?.base?.net))}</td>
                    </tr>
                )}
                {showUserRow && (
                    <tr>
                        <td className="text-muted" style={{ fontWeight: '400' }}>{usersLabelWithCount}</td>
                        {showDiscountColumns && (
                            <td className="text-end">
                                {hasDiscount(summary?.users?.discountPct)
                                    ? renderReadonlyField(renderMoney(summary?.users?.gross))
                                    : ""}
                            </td>
                        )}
                        {showDiscountColumns && (
                            <td className="text-end text-danger">
                                {renderReadonlyField(
                                    renderDiscount(summary?.users?.discountPct),
                                    "text-danger",
                                )}
                            </td>
                        )}
                        <td className="text-end">{renderReadonlyField(renderMoney(summary?.users?.net))}</td>
                    </tr>
                )}
                {showSiteRow && (
                    <tr>
                        <td className="text-muted" style={{ fontWeight: '400' }}>{sitesLabelWithCount}</td>
                        {showDiscountColumns && (
                            <td className="text-end">
                                {hasDiscount(summary?.sites?.discountPct)
                                    ? renderReadonlyField(renderMoney(summary?.sites?.gross))
                                    : ""}
                            </td>
                        )}
                        {showDiscountColumns && (
                            <td className="text-end text-danger">{renderReadonlyField(renderDiscount(summary?.sites?.discountPct), "text-danger")}</td>
                        )}
                        <td className="text-end">{renderReadonlyField(renderMoney(summary?.sites?.net))}</td>
                    </tr>
                )}
                {extraRows.map((row: any, index: number) => (
                    <tr key={`extra-${index}`}>
                        <td className="text-muted" style={{ fontWeight: '400' }}>{row.label}</td>
                        {showDiscountColumns && <td className="text-end"></td>}
                        {showDiscountColumns && <td className="text-end"></td>}
                        <td className="text-end">{renderReadonlyField(renderMoney(row.value))}</td>
                    </tr>
                ))}
                <tr>
                    <td className="text-muted" style={{ fontWeight: '400' }}>VAT @{Number(summary?.vat?.pct ?? 0).toFixed(2)}%</td>
                    {showDiscountColumns && <td className="text-end"></td>}
                    {showDiscountColumns && <td className="text-end"></td>}
                    <td className="text-end">{renderReadonlyField(renderMoney(summary?.vat?.amount))}</td>
                </tr>
                <tr className={totalRowClassName} style={totalRowStyle}>
                    <td className="fw-bold text-nowrap">{totalLabel}</td>
                    {showDiscountColumns && <td className="text-end"></td>}
                    {showDiscountColumns && <td className="text-end"></td>}
                    <td className={`fw-bold text-end ${totalValueClassName}`.trim()} style={{ color: "#00a38d" }}>
                        {renderReadonlyField(renderMoney(summary?.total?.net))}
                    </td>
                </tr>
            </tbody>
        </table>
    );

    if (noCard) {
        return table;
    }

    return (
        <>
            <Card className={`profile-project-card shadow-none material-shadow profile-project-primary mb-2`}>
                <CardBody>
                    <Row className='px-3'>
                        {table}
                    </Row>
                </CardBody>
            </Card>
        </>
    );
}

export default LicenseCost;
